import React, { useEffect, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import {
  createTryout, fetchTeamTryouts, closeTryout,
  fetchTryoutRegistrations, decideTryoutRegistration, rateTryoutRegistration,
} from "../lib/api";

/**
 * TryoutsTab — embedded in TeamHqModal.
 *
 * Owner/captain creates open tryouts, sees applicants, rates them 1-5,
 * approves (auto-adds to team) or rejects.
 * Non-admin members get a read-only view of upcoming tryouts.
 */
export function TryoutsTab({ hq, isAdmin, games, onToast, reloadHq }) {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try { setList(await fetchTeamTryouts(hq.team_id)); }
    catch { setList([]); }
  };
  useEffect(() => { load(); }, [hq.team_id]);   // eslint-disable-line

  const close = async (id) => {
    if (!window.confirm("Close this tryout? Applicants can no longer register.")) return;
    try {
      await closeTryout(id);
      onToast?.("Tryout closed.");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't close.");
    }
  };

  return (
    <div data-testid="team-hq-tryouts" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-bullseye mr-1" />OPEN TRYOUTS · {list.length}
        </p>
        {isAdmin && !creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="team-hq-tryout-new-btn"
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-neon"
          >
            <i className="fas fa-plus mr-1" />NEW TRYOUT
          </button>
        )}
      </div>

      {creating && (
        <TryoutCreateForm
          games={games}
          onCancel={() => setCreating(false)}
          onCreated={async () => { setCreating(false); await load(); }}
          teamId={hq.team_id}
          onToast={onToast}
        />
      )}

      {list.length === 0 && !creating ? (
        <p className="text-[12px] text-white/45 py-4 text-center" data-testid="team-hq-tryouts-empty">
          {isAdmin ? "No tryouts yet. Hit + NEW TRYOUT to host one." : "No open tryouts."}
        </p>
      ) : list.map((t) => (
        <TryoutCard
          key={t.tryout_id}
          tryout={t}
          isAdmin={isAdmin}
          open={openId === t.tryout_id}
          onToggle={() => setOpenId(openId === t.tryout_id ? null : t.tryout_id)}
          onClose={() => close(t.tryout_id)}
          onChange={async () => { await load(); await reloadHq?.(); }}
          onToast={onToast}
        />
      ))}
    </div>
  );
}

function TryoutCreateForm({ games, onCancel, onCreated, teamId, onToast }) {
  const [form, setForm] = useState({
    title: "",
    game: games[0]?.name || "",
    role: "",
    description: "",
    starts_at: "",
    max_slots: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.game.trim()) {
      onToast?.("Title and game are required.");
      return;
    }
    setBusy(true);
    try {
      await createTryout(teamId, {
        title: form.title.trim(),
        game: form.game.trim(),
        role: form.role.trim(),
        description: form.description.trim(),
        starts_at: form.starts_at || null,
        max_slots: form.max_slots ? Number(form.max_slots) : null,
      });
      onToast?.("🎯 Tryout posted.");
      onCreated();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't post tryout.");
    } finally { setBusy(false); }
  };
  return (
    <form
      onSubmit={submit}
      data-testid="team-hq-tryout-form"
      className="glass rounded-2xl p-3 border border-[#14b8a6]/40 space-y-2"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">NEW TRYOUT</p>
      <input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Tryout title (e.g. Scrim night — Duelist tryouts)"
        data-testid="team-hq-tryout-title"
        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
      />
      <div className="grid sm:grid-cols-3 gap-2">
        <select
          value={form.game}
          onChange={(e) => setForm({ ...form, game: e.target.value })}
          data-testid="team-hq-tryout-game"
          className="bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        >
          {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
        </select>
        <input
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          placeholder="Role (IGL, Sub…)"
          data-testid="team-hq-tryout-role"
          className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
        />
        <input
          type="number"
          min={1} max={200}
          value={form.max_slots}
          onChange={(e) => setForm({ ...form, max_slots: e.target.value })}
          placeholder="Max slots (opt)"
          data-testid="team-hq-tryout-slots"
          className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
        />
      </div>
      <input
        type="datetime-local"
        value={form.starts_at}
        onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
        data-testid="team-hq-tryout-starts"
        className="w-full bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
      />
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Format, requirements, links…"
        rows={3}
        data-testid="team-hq-tryout-description"
        className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="team-hq-tryout-cancel"
          className="text-[11px] tracking-widest px-4 py-1.5 rounded-xl btn-ghost"
        >
          CANCEL
        </button>
        <button
          type="submit"
          disabled={busy}
          data-testid="team-hq-tryout-save"
          className="text-[11px] tracking-widest px-4 py-1.5 rounded-xl btn-neon disabled:opacity-60"
        >
          {busy ? "..." : "POST TRYOUT"}
        </button>
      </div>
    </form>
  );
}

function TryoutCard({ tryout, isAdmin, open, onToggle, onClose, onChange, onToast }) {
  const [regs, setRegs] = useState(null);
  const [busyId, setBusyId] = useState("");
  const loadRegs = async () => {
    try { setRegs(await fetchTryoutRegistrations(tryout.tryout_id)); }
    catch { setRegs([]); }
  };
  useEffect(() => { if (open && isAdmin) loadRegs(); /* eslint-disable-next-line */ }, [open]);

  const decide = async (uid, action) => {
    setBusyId(uid);
    try {
      await decideTryoutRegistration(tryout.tryout_id, uid, action);
      onToast?.(action === "approve" ? "✅ Approved + added to team." : "Rejected.");
      await loadRegs();
      await onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't decide.");
    } finally { setBusyId(""); }
  };

  const rate = async (uid, rating) => {
    try {
      await rateTryoutRegistration(tryout.tryout_id, uid, rating);
      await loadRegs();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't rate.");
    }
  };

  return (
    <div
      data-testid={`team-hq-tryout-${tryout.tryout_id}`}
      className="glass rounded-2xl p-3 border border-[#14b8a6]/30"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-gamepad mr-1" />{tryout.game}
            {tryout.role && <span className="ml-2 text-[#a3a3ac]">· {tryout.role}</span>}
          </p>
          <p className="font-bold text-sm truncate">{tryout.title}</p>
          {tryout.description && (
            <p className="text-[11px] text-white/70 mt-0.5 line-clamp-2">{tryout.description}</p>
          )}
          <div className="flex gap-3 mt-1 text-[10px] text-white/55">
            {tryout.starts_at && <span><i className="fas fa-calendar mr-1" />{new Date(tryout.starts_at).toLocaleString()}</span>}
            {tryout.max_slots ? <span><i className="fas fa-users mr-1" />{tryout.applicant_count || 0}/{tryout.max_slots}</span>
              : <span><i className="fas fa-users mr-1" />{tryout.applicant_count || 0} applicant{tryout.applicant_count === 1 ? "" : "s"}</span>}
            {tryout.pending_count > 0 && <span className="text-[#14b8a6] font-bold">{tryout.pending_count} pending</span>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onToggle}
              data-testid={`team-hq-tryout-applicants-${tryout.tryout_id}`}
              className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg btn-ghost"
            >
              {open ? "HIDE" : "APPLICANTS"}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid={`team-hq-tryout-close-${tryout.tryout_id}`}
              className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
            >
              CLOSE
            </button>
          </div>
        )}
      </div>
      {open && isAdmin && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2"
             data-testid={`team-hq-tryout-regs-${tryout.tryout_id}`}>
          {regs === null ? (
            <p className="text-[11px] text-white/40">Loading applicants…</p>
          ) : regs.length === 0 ? (
            <p className="text-[11px] text-white/40">No applicants yet.</p>
          ) : regs.map((r) => (
            <div key={r.registration_id} data-testid={`team-hq-tryout-reg-${r.registration_id}`}
                 className="flex items-center gap-2 p-2 rounded-xl bg-black/30 border border-white/10">
              <AvatarChip avatarId={r.user_avatar_id} pfpUrl={r.user_pfp_url} size={32} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-bold truncate">{r.user_name}</p>
                <p className="text-[10px] text-[#a3a3ac]">
                  MMR {r.user_elo || "—"} ·
                  <span className={`ml-1 font-bold ${
                    r.status === "approved" ? "text-[#14b8a6]" :
                    r.status === "rejected" ? "text-[#14b8a6]" : "text-[#14b8a6]"
                  }`}>{r.status.toUpperCase()}</span>
                </p>
                {r.note && <p className="text-[10px] text-white/65 truncate">"{r.note}"</p>}
              </div>
              <div className="flex items-center gap-0.5 mr-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => rate(r.user_id, n)}
                    data-testid={`team-hq-tryout-rate-${r.user_id}-${n}`}
                    title={`Rate ${n} star${n === 1 ? "" : "s"}`}
                    className={`text-[10px] ${r.rating >= n ? "text-[#14b8a6]" : "text-white/30 hover:text-[#14b8a6]"}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              {r.status === "pending" && (
                <>
                  <button
                    type="button"
                    onClick={() => decide(r.user_id, "approve")}
                    disabled={busyId === r.user_id}
                    data-testid={`team-hq-tryout-approve-${r.user_id}`}
                    title="Approve + add to team"
                    className="w-7 h-7 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center"
                  >
                    <i className="fas fa-check text-[10px]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(r.user_id, "reject")}
                    disabled={busyId === r.user_id}
                    data-testid={`team-hq-tryout-reject-${r.user_id}`}
                    title="Reject"
                    className="w-7 h-7 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center"
                  >
                    <i className="fas fa-xmark text-[10px]" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TryoutsTab;
